import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { B } from '../constants';
import { blogs } from '../api';
import { trackEvent } from '../lib/analytics';
import { trackActivity } from '../lib/activityEvents';
import Input from '../components/ui/Input';

export default function BlogEditorScreen({ trip, goScreen, showToast }) {
  const { tripId: routeTripId } = useParams();
  const [title, setTitle] = useState(trip?.blog?.title || '');
  const [content, setContent] = useState(trip?.blog?.content || '');
  const [isPublished, setIsPublished] = useState(trip?.blog?.isPublished || false);
  const [saving, setSaving] = useState(false);

  const handleSave = async (publish = false) => {
    if (!title.trim()) {
      showToast('Title is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const data = { 
        title, 
        content, 
        isPublished: publish || isPublished 
      };
      
      let updated;
      if (trip?.blog?.id) {
        updated = await blogs.update(trip.id, data);
      } else {
        updated = await blogs.create({ tripId: trip.id, ...data });
      }
      
      setIsPublished(updated.isPublished);
      if (publish) trackEvent('blog_published', { tripId: trip?.id });
      if (publish) trackActivity('content', 'blog_published', { tripId: trip?.id ?? routeTripId ?? null });
      showToast(publish ? 'Blog published!' : 'Blog saved!');
    } catch (err) {
      showToast(err.message || 'Failed to save blog', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFormat = (format) => {
    const textarea = document.getElementById('blog-content');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `_${selectedText}_`;
        break;
      case 'heading':
        formattedText = `\n## ${selectedText}`;
        break;
      case 'list':
        formattedText = `\n- ${selectedText}`;
        break;
      default:
        return;
    }
    
    setContent(content.substring(0, start) + formattedText + content.substring(end));
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 20px' }}>
      <button 
        onClick={() => goScreen('tripDetail', trip)}
        style={{ background: 'none', border: 'none', color: B.muted, cursor: 'pointer', marginBottom: 16, fontSize: 14 }}
      >
        ← Back to Trip
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, margin: 0 }}>
          📝 Blog Editor
        </h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            style={{
              padding: '10px 20px', background: '#f0f0f0', color: B.night,
              border: 'none', borderRadius: 20, cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 13
            }}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            style={{
              padding: '10px 20px', background: isPublished ? B.forest : B.sky,
              color: 'white', border: 'none', borderRadius: 20, cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 13
            }}
          >
            {isPublished ? '✅ Published' : 'Publish'}
          </button>
        </div>
      </div>

      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Blog Title..."
        style={{
          width: '100%', padding: '16px 20px', borderRadius: 12, border: '1px solid #ddd',
          fontSize: 24, fontWeight: 700, fontFamily: "'Playfair Display',serif",
          marginBottom: 16
        }}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={() => handleFormat('bold')} style={formatBtnStyle}>**B**</button>
        <button onClick={() => handleFormat('italic')} style={formatBtnStyle}>_I_</button>
        <button onClick={() => handleFormat('heading')} style={formatBtnStyle}>H2</button>
        <button onClick={() => handleFormat('list')} style={formatBtnStyle}>• List</button>
      </div>

      <textarea
        id="blog-content"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Write your blog post here...&#10;&#10;Use **text** for bold, _text_ for italic, ## for headings, - for lists"
        style={{
          width: '100%', minHeight: 400, padding: '20px', borderRadius: 12,
          border: '1px solid #ddd', fontSize: 16, fontFamily: "'Montserrat',sans-serif",
          lineHeight: 1.8, resize: 'vertical'
        }}
      />

      <div style={{ marginTop: 20, padding: 16, background: '#f9f9f9', borderRadius: 12 }}>
        <h3 style={{ marginTop: 0, fontSize: 14, fontWeight: 600 }}>Tips:</h3>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: B.muted }}>
          <li>Use **text** for bold</li>
          <li>Use _text_ for italic</li>
          <li>Use ## for headings</li>
          <li>Use - for bullet points</li>
        </ul>
      </div>
    </div>
  );
}

const formatBtnStyle = {
  padding: '6px 12px',
  background: 'white',
  border: '1px solid #ddd',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600
};